import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../../services/api';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Paper,
  Alert,
  CircularProgress,
  Stack,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormGroup,
  FormLabel,
  Grid,
  Divider,
} from '@mui/material';
import { gradientHero } from '../../theme';
import PublicNavBar from '../../components/PublicNavBar';
import PortalFooter from '../../components/PortalFooter';

const GESELLSCHAFTSFORMEN = [
  'GmbH', 'UG (haftungsbeschränkt)', 'AG', 'GmbH & Co. KG', 'KG', 'GbR', 'OHG',
  'E.K. (Einzelkaufmann)', 'Einzelunternehmen', 'Ltd.', 'SE', 'Sonstige',
];
const ANREDEN = ['Herr', 'Frau', 'Keine Angabe'];
const LAENDER = ['Deutschland', 'Österreich', 'Schweiz', 'EU-Ausland', 'Nicht-EU-Ausland'];
const INBOUND_TYPEN = [
  'Bestellannahme', 'Helpdesk / Technische Hotline', 'Sekretariatsservice',
  'Reservierung / Ticketing', 'Notfallservice / Notruf', 'Sonstiges',
];
const OUTBOUND_TYPEN = [
  'Adressqualifizierung', 'Kundenzufriedenheitsbefragung', 'Leadmanagement',
  'Marktforschung', 'Mystery Calls', 'Telemarketing', 'Telesales (Verkauf am Telefon)',
  'Terminvereinbarung', 'Sonstiges',
];
const SPRACHEN = ['Deutsch', 'Englisch', 'Spanisch', 'Französisch', 'Italienisch', 'Weitere Fremdsprachen'];
const VERGUETUNGSMODELLE = [
  'Auf Stundenbasis', 'Je Anruf', 'Nach Zeitaufwand', 'CPO (Cash per Order)',
  'Je Terminvereinbarung', 'Mischkalkulation / Shared Risk', 'Verhandelbar – offen für Vorschläge',
];
const SERVICEZEITEN_OPT = [
  '24 Stunden / 7 Tage pro Woche',
  'Mo.–Fr. normale Bürozeiten',
  'Sa. und/oder So.',
];
const MITARBEITER_OPT = ['1–10', '11–50', '51–200', '> 200'];

const RegisterContractorPage: React.FC = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    vorname: '',
    nachname: '',
    email: '',
    password: '',
    password_confirmation: '',
    anrede: '',
    position: '',
    telefon: '',
    mobil: '',
    fax: '',
    company_name: '',
    gesellschaftsform: '',
    strasse: '',
    plz: '',
    ort: '',
    land: 'Deutschland',
    homepage: '',
    ust_id: '',
    mitarbeiter: '',
    bietet_inbound: false,
    bietet_outbound: false,
    inbound_typen: [] as string[],
    outbound_typen: [] as string[],
    servicezeiten: [] as string[],
    sprachen: [] as string[],
    verguetungsmodelle: [] as string[],
    agree_to_terms: false,
  });

  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [registered, setRegistered] = useState(false);

  const handleChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleArrayToggle = (field: keyof typeof formData, value: string) => {
    setFormData(prev => {
      const arr = prev[field] as string[];
      return { ...prev, [field]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.agree_to_terms) {
      setError('Sie müssen den Bedingungen zustimmen.');
      return;
    }
    if (formData.password !== formData.password_confirmation) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }
    if (!formData.bietet_inbound && !formData.bietet_outbound) {
      setError('Bitte wählen Sie mindestens eine Dienstleistungsart (Inbound oder Outbound).');
      return;
    }

    const servicesSummary = [
      formData.bietet_inbound && formData.inbound_typen.length && `Inbound: ${formData.inbound_typen.join(', ')}`,
      formData.bietet_outbound && formData.outbound_typen.length && `Outbound: ${formData.outbound_typen.join(', ')}`,
      formData.servicezeiten.length && `Zeiten: ${formData.servicezeiten.join(', ')}`,
      formData.sprachen.length && `Sprachen: ${formData.sprachen.join(', ')}`,
      formData.verguetungsmodelle.length && `Vergütung: ${formData.verguetungsmodelle.join(', ')}`,
      formData.mitarbeiter && `Mitarbeiter: ${formData.mitarbeiter}`,
    ].filter(Boolean).join(' | ');

    setIsLoading(true);
    try {
      await authAPI.register({
        name: `${formData.vorname} ${formData.nachname}`.trim(),
        email: formData.email,
        password: formData.password,
        password_confirmation: formData.password_confirmation,
        phone: formData.telefon,
        company_name: formData.company_name,
        role: 'contractor',
        services: servicesSummary,
      });
      setRegistered(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Registrierung fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <Box sx={{ mt: 5, mb: 2.5 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 1 }}>
        {children}
      </Typography>
      <Divider sx={{ borderColor: '#E85D26', borderBottomWidth: 2, width: 40 }} />
    </Box>
  );

  const CheckboxGroup = ({
    label,
    options,
    field,
  }: {
    label: string;
    options: string[];
    field: keyof typeof formData;
  }) => (
    <FormControl component="fieldset" sx={{ width: '100%', mb: 2 }}>
      <FormLabel sx={{ fontWeight: 600, color: '#333', mb: 1, '&.Mui-focused': { color: '#333' } }}>
        {label}
      </FormLabel>
      <FormGroup row>
        {options.map(opt => (
          <FormControlLabel
            key={opt}
            control={
              <Checkbox
                checked={(formData[field] as string[]).includes(opt)}
                onChange={() => handleArrayToggle(field, opt)}
                disabled={isLoading}
                sx={{ '&.Mui-checked': { color: '#E85D26' } }}
              />
            }
            label={opt}
            sx={{ minWidth: { xs: '100%', sm: '48%' }, mr: 0 }}
          />
        ))}
      </FormGroup>
    </FormControl>
  );

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f4f6f9' }}>
      <PublicNavBar />

      <Box sx={{ background: gradientHero, color: '#fff', py: 6, textAlign: 'center' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h1" sx={{ fontWeight: 800, mb: 1.5 }}>
            Als Dienstleister registrieren
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.85, fontWeight: 400 }}>
            Registrieren Sie Ihr Call Center kostenlos und erhalten Sie Zugang zu geprüften Auftragsanfragen
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper elevation={0} sx={{ p: { xs: 3, sm: 4, md: 6 }, border: '1px solid #e0e0e0', borderRadius: 2 }}>

          {registered ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
                <strong>Vielen Dank für Ihre Registrierung!</strong><br />
                Ihre Angaben wurden erfolgreich übermittelt und werden von unserem Team geprüft. Sie erhalten eine Bestätigung per E-Mail, sobald Ihr Konto freigeschaltet wurde.
              </Alert>
              <Button variant="outlined" onClick={() => navigate('/')} sx={{ mt: 2 }}>
                Zur Startseite
              </Button>
            </Box>
          ) : (
          <>
          <Alert severity="info" sx={{ mb: 4 }}>
            Die Registrierung ist kostenlos und verpflichtet Sie zu keinerlei Zahlungen. Ihre Angaben werden von unserem Team geprüft – Sie erhalten eine Bestätigung per E-Mail sobald Ihr Konto freigeschaltet wurde.
          </Alert>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          <form onSubmit={handleSubmit}>

            <SectionTitle>1. Zugangsdaten</SectionTitle>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth label="Vorname *" name="vorname" value={formData.vorname} onChange={handleChange} required disabled={isLoading} variant="outlined" />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth label="Nachname *" name="nachname" value={formData.nachname} onChange={handleChange} required disabled={isLoading} variant="outlined" />
              </Grid>
              <Grid size={12}>
                <TextField fullWidth label="E-Mail-Adresse *" name="email" type="email" value={formData.email} onChange={handleChange} required disabled={isLoading} placeholder="ihre@email.de" variant="outlined" />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth label="Passwort *" name="password" type="password" value={formData.password} onChange={handleChange} required disabled={isLoading} helperText="Mindestens 8 Zeichen" variant="outlined" />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth label="Passwort bestätigen *" name="password_confirmation" type="password" value={formData.password_confirmation} onChange={handleChange} required disabled={isLoading} variant="outlined" />
              </Grid>
            </Grid>

            <SectionTitle>2. Kontaktdaten</SectionTitle>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>Anrede</InputLabel>
                  <Select name="anrede" value={formData.anrede} onChange={handleChange} label="Anrede" disabled={isLoading}>
                    {ANREDEN.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 8 }}>
                <TextField fullWidth label="Position im Unternehmen" name="position" value={formData.position} onChange={handleChange} disabled={isLoading} placeholder="z.B. Geschäftsführer, Vertriebsleiter" variant="outlined" />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField fullWidth label="Telefon *" name="telefon" value={formData.telefon} onChange={handleChange} required disabled={isLoading} placeholder="+49 30 123456" variant="outlined" />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField fullWidth label="Mobil" name="mobil" value={formData.mobil} onChange={handleChange} disabled={isLoading} placeholder="+49 170 123456" variant="outlined" />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField fullWidth label="Fax" name="fax" value={formData.fax} onChange={handleChange} disabled={isLoading} variant="outlined" />
              </Grid>
            </Grid>

            <SectionTitle>3. Unternehmensdaten</SectionTitle>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 8 }}>
                <TextField fullWidth label="Firmenname *" name="company_name" value={formData.company_name} onChange={handleChange} required disabled={isLoading} placeholder="Name Ihres Call Centers" variant="outlined" />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>Gesellschaftsform</InputLabel>
                  <Select name="gesellschaftsform" value={formData.gesellschaftsform} onChange={handleChange} label="Gesellschaftsform" disabled={isLoading}>
                    {GESELLSCHAFTSFORMEN.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={12}>
                <TextField fullWidth label="Straße / Nr." name="strasse" value={formData.strasse} onChange={handleChange} disabled={isLoading} variant="outlined" />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField fullWidth label="PLZ" name="plz" value={formData.plz} onChange={handleChange} disabled={isLoading} variant="outlined" />
              </Grid>
              <Grid size={{ xs: 12, sm: 5 }}>
                <TextField fullWidth label="Ort" name="ort" value={formData.ort} onChange={handleChange} disabled={isLoading} variant="outlined" />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>Land</InputLabel>
                  <Select name="land" value={formData.land} onChange={handleChange} label="Land" disabled={isLoading}>
                    {LAENDER.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth label="Homepage" name="homepage" value={formData.homepage} onChange={handleChange} disabled={isLoading} placeholder="https://www.beispiel.de" variant="outlined" />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth label="Umsatzsteuer-ID (nur EU)" name="ust_id" value={formData.ust_id} onChange={handleChange} disabled={isLoading} placeholder="z.B. DE123456789" variant="outlined" />
              </Grid>
            </Grid>

            <SectionTitle>4. Angebotene Dienstleistungen</SectionTitle>

            <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
              <FormLabel sx={{ fontWeight: 600, color: '#333', mb: 1, '&.Mui-focused': { color: '#333' } }}>
                Sie bieten an, Anrufe... *
              </FormLabel>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="bietet_inbound"
                      checked={formData.bietet_inbound}
                      onChange={handleChange}
                      disabled={isLoading}
                      sx={{ '&.Mui-checked': { color: '#E85D26' } }}
                    />
                  }
                  label="Entgegenzunehmen (Inbound)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      name="bietet_outbound"
                      checked={formData.bietet_outbound}
                      onChange={handleChange}
                      disabled={isLoading}
                      sx={{ '&.Mui-checked': { color: '#E85D26' } }}
                    />
                  }
                  label="Aktiv zu tätigen (Outbound / Rebound)"
                />
              </FormGroup>
            </FormControl>

            {formData.bietet_inbound && (
              <Box sx={{ pl: 2, borderLeft: '3px solid #E85D26', mb: 3 }}>
                <CheckboxGroup label="Welche Arten von Inbound bieten Sie an?" options={INBOUND_TYPEN} field="inbound_typen" />
              </Box>
            )}

            {formData.bietet_outbound && (
              <Box sx={{ pl: 2, borderLeft: '3px solid #533483', mb: 3 }}>
                <CheckboxGroup label="Welche Arten von Outbound bieten Sie an?" options={OUTBOUND_TYPEN} field="outbound_typen" />
              </Box>
            )}

            <CheckboxGroup label="Zu welchen Zeiten ist Ihr Callcenter besetzt?" options={SERVICEZEITEN_OPT} field="servicezeiten" />

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Anzahl Mitarbeiter (VZÄ)</InputLabel>
                  <Select name="mitarbeiter" value={formData.mitarbeiter} onChange={handleChange} label="Anzahl Mitarbeiter (VZÄ)" disabled={isLoading}>
                    {MITARBEITER_OPT.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <CheckboxGroup label="Welche Sprachen bieten Sie an? *" options={SPRACHEN} field="sprachen" />

            <CheckboxGroup label="Akzeptierte Vergütungsmodelle" options={VERGUETUNGSMODELLE} field="verguetungsmodelle" />

            <Divider sx={{ my: 4 }} />

            <FormControlLabel
              control={
                <Checkbox
                  name="agree_to_terms"
                  checked={formData.agree_to_terms}
                  onChange={handleChange}
                  disabled={isLoading}
                  sx={{ '&.Mui-checked': { color: '#E85D26' } }}
                />
              }
              label={
                <Typography variant="body2">
                  Ich habe die{' '}
                  <Link to="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#E85D26', fontWeight: 600 }}>
                    AGB
                  </Link>
                  {' '}und die{' '}
                  <Link to="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#E85D26', fontWeight: 600 }}>
                    Datenschutzerklärung
                  </Link>
                  {' '}gelesen und stimme diesen zu. *
                </Typography>
              }
              sx={{ mb: 3, alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0.5 } }}
            />

            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={isLoading}
              size="large"
              sx={{
                background: 'linear-gradient(135deg, #E85D26 0%, #c94d1e 100%)',
                fontWeight: 700,
                py: 1.5,
                fontSize: '1rem',
                '&:hover': { background: 'linear-gradient(135deg, #d94f1e 0%, #b8431a 100%)' },
              }}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Jetzt kostenlos registrieren'}
            </Button>

          </form>

          <Stack spacing={1.5} sx={{ mt: 4 }}>
            <Typography textAlign="center" color="textSecondary" variant="body2">
              Bereits ein Konto?{' '}
              <Link to="/login" style={{ color: '#E85D26', fontWeight: 600, textDecoration: 'none' }}>
                Hier anmelden
              </Link>
            </Typography>
            <Typography textAlign="center" color="textSecondary" variant="body2">
              Suchen Sie ein Call Center?{' '}
              <Button
                component="span"
                onClick={() => navigate('/register/customer')}
                sx={{ color: '#E85D26', fontWeight: 600, p: 0, textTransform: 'none', fontSize: 'inherit', minWidth: 0 }}
              >
                Zur Auftraggeber-Registrierung
              </Button>
            </Typography>
          </Stack>
          </>
          )}
        </Paper>
      </Container>

      <PortalFooter />
    </Box>
  );
};

export default RegisterContractorPage;
